/**
 * Быстрое переключение статуса заказа прямо в таблице.
 *
 * Смысл — не открывать панель заказа ради одного клика: техник за день
 * переводит десятки нарядов и делает это из списка.
 *
 * Выпадающий список позиционируется ФИКСИРОВАННО, а не внутри ячейки.
 * Таблица заказов прокручивается по горизонтали (`overflow-x-auto`), и обычный
 * абсолютно спозиционированный список обрезался бы контейнером — на узких
 * экранах пользователь видел бы половину вариантов.
 *
 * Выбор меняет ЧЕРНОВИК (§6): правка видна сразу, у пункта меню загорается
 * точка, а в базу уходит по кнопке «Сохранить изменения». Заказы намеренно
 * оставлены под этой защитой — решение пересматривали и подтвердили.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { urgencyOf, type OrderStatus, type Urgency } from '@otc/shared';
import { Badge } from './ui.js';
import { STATUS_COLORS } from '../theme.js';
import { viewport } from './usePopover.js';

export interface StatusOption {
  label: string;
  status: OrderStatus;
  /** `null` — сохранить текущее значение срочности заказа. */
  urgency: Urgency | null;
  color: string;
  hint?: string;
}

/**
 * Набор вариантов.
 *
 * «В работе» задаётся и статусом, и срочностью одновременно: если оставить
 * срочность «Авто», подпись пересчиталась бы по дате сдачи и показала,
 * например, «Срочно» — то есть не то, что пользователь только что выбрал.
 * Выбранное и показанное обязаны совпадать, иначе виджету нельзя доверять.
 */
const OPTIONS: StatusOption[] = [
  {
    label: 'Авто (по дате сдачи)',
    status: 'В очереди',
    urgency: 'Авто',
    color: STATUS_COLORS.normal,
    hint: 'Срочность считается сама',
  },
  { label: 'Очень срочно', status: 'В очереди', urgency: 'Очень срочно', color: STATUS_COLORS.critical },
  { label: 'Срочно', status: 'В очереди', urgency: 'Срочно', color: STATUS_COLORS.urgent },
  { label: 'В работе', status: 'В работе', urgency: 'В работе', color: STATUS_COLORS.normal },
  {
    label: 'Ожидание',
    status: 'Ожидание',
    urgency: null,
    color: STATUS_COLORS.hold,
    hint: 'Врач попросил подождать',
  },
  { label: 'Готово', status: 'Готово', urgency: null, color: STATUS_COLORS.done },
];

interface Position {
  top: number;
  left: number;
  width: number;
}

const MENU_WIDTH = 248;
/** Оценка до первого измерения: список ставится по-настоящему в `useLayoutEffect`. */
const MENU_HEIGHT_GUESS = 240;
const GAP = 4;

/**
 * Координаты списка с переворотом вверх, если снизу не помещается.
 *
 * Высота передаётся ИЗМЕРЕННАЯ, а не угаданная. С постоянной 200 пикселей
 * список переворачивался вверх там, где снизу места хватало: он вылезал над
 * строкой и закрывал соседние, а щелчок «чуть выше» попадал уже в него.
 */
/**
 * `keepOffscreen` — что делать, когда строка вышла за пределы окна.
 *
 * При прокрутке это значит «ушёл от неё сам», и список закрывается. При
 * изменении размера окна строку вытеснила клавиатура или адресная строка —
 * тогда список остаётся и просто прижимается к краю видимой области.
 */
function positionFor(trigger: HTMLElement, height: number, keepOffscreen = false): Position | null {
  const rect = trigger.getBoundingClientRect();
  const view = viewport();

  if (!keepOffscreen && (rect.bottom < 0 || rect.top > view.height)) return null;

  const spaceBelow = view.height - rect.bottom;
  const openUpwards = spaceBelow < height + GAP && rect.top > height + GAP;
  const wanted = openUpwards ? rect.top - height - GAP : rect.bottom + GAP;

  return {
    // Ни один край не выпускаем за окно: на телефоне список иначе уезжал
    // под нижнюю кромку вместе с половиной вариантов.
    top: Math.max(8, Math.min(wanted, view.height - height - 8)),
    left: Math.max(8, Math.min(rect.left, view.width - MENU_WIDTH - 8)),
    width: MENU_WIDTH,
  };
}

export function StatusQuickPicker({
  order,
  today,
  onChange,
  disabled,
}: {
  order: { status: OrderStatus; urgency: Urgency; due: string | null };
  today: string;
  onChange: (patch: { status: OrderStatus; urgency: Urgency }) => void;
  disabled?: boolean;
}) {
  const [position, setPosition] = useState<Position | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const current = urgencyOf(order, today);
  const open = position !== null;

  /*
   * Второй проход: список уже нарисован, его высоту можно измерить и поставить
   * точно. Обновление только при РАСХОЖДЕНИИ — иначе каждый пересчёт вызывал
   * бы новый рендер и цикл не сошёлся бы.
   */
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) return;
    const next = positionFor(triggerRef.current, menuRef.current.offsetHeight);
    setPosition((current) =>
      next && current && current.top === next.top && current.left === next.left ? current : next,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Закрытие по нажатию мимо и по Escape; прокрутка и изменение размера окна
   * список ПЕРЕДВИГАЮТ.
   *
   * Закрывать по ним нельзя: на телефоне размер окна меняет и адресная строка
   * при прокрутке, и экранная клавиатура — список закрывался сам, едва
   * открывшись. Он висит в фиксированных координатах, поэтому просто
   * пересчитывается по строке, к которой относится.
   */
  useEffect(() => {
    if (!open) return;

    const close = () => setPosition(null);

    /** Нажатие пришлось внутрь списка или его кнопки? */
    const inside = (target: EventTarget | null): boolean =>
      target instanceof Node &&
      (menuRef.current?.contains(target) === true || triggerRef.current?.contains(target) === true);

    const onPointerDown = (event: Event) => {
      if (!inside(event.target)) close();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    /**
     * Пересчёт места. Прокрутка внутри самого списка его не двигает.
     *
     * Цель события проверяется через `instanceof Node`: у `resize` она —
     * сам `window`, и `contains` на нём бросает исключение прямо в
     * обработчике, обрывая пересчёт.
     */
    const move = (keepOffscreen: boolean) => {
      const trigger = triggerRef.current;
      const height = menuRef.current?.offsetHeight ?? MENU_HEIGHT_GUESS;
      setPosition(trigger ? positionFor(trigger, height, keepOffscreen) : null);
    };

    /** Прокрутка страницы; листание самого списка его не двигает. */
    const onScroll = (event: Event) => {
      const target = event.target;
      if (target instanceof Node && menuRef.current?.contains(target)) return;
      move(false);
    };

    /** Изменение размера окна — клавиатура или адресная строка телефона. */
    const onResize = () => move(true);

    document.addEventListener('mousedown', onPointerDown);
    // Касание: на телефоне до `mousedown` дело доходит не всегда.
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('scroll', onResize);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('scroll', onResize);
    };
  }, [open]);

  const select = (option: StatusOption) => {
    onChange({
      status: option.status,
      // `null` — оставить текущую срочность: «Готово» её не перетирает,
      // чтобы при возврате в работу не потерялась ручная пометка.
      urgency: option.urgency ?? order.urgency,
    });
    setPosition(null);
  };

  return (
    <>
      <button
        ref={triggerRef}
        disabled={disabled}
        title={disabled ? undefined : 'Нажмите, чтобы изменить статус'}
        onClick={(event) => {
          // Иначе клик «провалится» в строку и откроет панель заказа.
          event.stopPropagation();
          if (disabled) return;
          setPosition(open ? null : positionFor(event.currentTarget, MENU_HEIGHT_GUESS));
        }}
        className="inline-flex max-w-full items-center gap-1 overflow-hidden rounded hover:opacity-75 disabled:opacity-100"
      >
        {/* Отдельной пометки о просрочке нет: заказ с прошедшим сроком и так
            попадает в «Очень срочно», а второй значок рядом только шумел. */}
        <Badge color={STATUS_COLORS[current.tone]}>{current.label}</Badge>
      </button>

      {/*
        Список рисуется В КОРНЕ СТРАНИЦЫ, а не рядом с плашкой.

        Он стоит в координатах окна (`position: fixed`), а журнал заказов
        показывается с масштабом. `zoom` у предка умножает координаты и
        фиксированных потомков: при 80 % список уезжал вверх на пятую часть
        высоты экрана и открывался далеко от кнопки, по которой нажали.
        При 100 % совпадало, поэтому долго не было видно.
      */}
      {position && createPortal(
        <div
          ref={menuRef}
          data-testid="status-menu"
          role="menu"
          className="flex flex-col rounded-lg border py-1 shadow-lg"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: position.width,
            zIndex: 60,
            background: 'var(--otc-surface)',
            borderColor: 'var(--otc-line)',
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {/*
            Строки РАЗМАШИСТЫЕ, а не плотные. Пять пунктов по тридцать
            пикселей стояли вплотную, и промах на несколько пикселей менял
            статус на соседний — а это ошибка, которую видно только потом.
            
            «Авто» отделено чертой: оно задаёт не статус, а способ его
            вычисления, и в общем ряду читалось как пятый вариант статуса.
          */}
          {OPTIONS.map((option, index) => {
            const active =
              option.status === order.status &&
              (option.urgency === null || option.urgency === order.urgency);

            return (
              <button
                key={option.label}
                onClick={() => select(option)}
                className="flex items-center gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-black/5"
                style={{
                  background: active ? 'rgba(0,0,0,0.05)' : undefined,
                  borderTop: index === 1 ? '1px solid var(--otc-line)' : undefined,
                  marginTop: index === 1 ? 4 : undefined,
                  paddingTop: index === 1 ? 10 : undefined,
                }}
              >
                <span
                  className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
                  style={{ background: option.color }}
                />
                <span className="flex-1">
                  <span className="block text-ink">{option.label}</span>
                  {option.hint && <span className="block text-[11px] text-ink2">{option.hint}</span>}
                </span>
                {active && <span className="text-xs text-ink2">✓</span>}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}
